import { ChangeEvent, useEffect, useState } from "react";
import Swal from "sweetalert2";
import * as yup from "yup";
import { useForm, Controller } from "react-hook-form";
import { useNavigate } from "react-router-dom";
import { yupResolver } from "@hookform/resolvers/yup";
import { IUser, UserFormData } from "../../../../utils/api/user/user.type";
import { cleannerError, useUser, validatePhone } from "../../../../utils/Utils";
import { useAddOrEditPatientMutation } from "../../../../utils/api/user/user.api";
import { isCabinet, isMedecin, isSecretaire } from "../../../../routes/routerUtils";
import { Data } from "../../../common/GoogleInput";
import { Color } from "../../../../utils/theme";

function UseAddOrEditPatient(item?: IUser) {
  const [address, setAddress] = useState("");
  const [phone, setPhone] = useState<any>();
  const [code, setCode] = useState("");
  const [file, setFile] = useState<any>(null)
  const validationSchema = yup.object().shape({
    nom: yup.string().required().label("Le nom"),
    prenom: yup.string().required().label("Le prénom"),
    civilite: yup.string().required().label("Le genre"),
    adresse: yup.string().required().label("L'adresse"),
    telephone: validatePhone(yup, code).required(),
    email: yup.string().email().required().label("L'email"),
    fichier: yup.mixed().label("Le dossier").nullable(),
    date_de_naissance: yup.string().required().label("La date de naissance").nullable(),
  });

  const {
    register,
    handleSubmit,
    formState: { errors },
    setValue,
    clearErrors,
    control,
  } = useForm<UserFormData>({
    resolver: yupResolver(validationSchema),
  });
  const user = useUser();
  const navigate = useNavigate();
  const [sendData, { isLoading, isSuccess, isError, error }] =
    useAddOrEditPatientMutation();

  useEffect(() => {
    if (isCabinet(user) && !item?.structure) {
      setValue("structure", user?.id);
    }

    if (isMedecin(user)) {
      setValue("structure", user?.structure?.id);
      setValue("parent", user?.id);
    }

    if (isSecretaire(user)) {
      setValue("structure", user?.structure?.id);
      setValue("created_by_secretaire",user?.id);
      setValue("parent", user?.id);
    }

  }, [user]);

  const onChangeFile = (e:ChangeEvent<HTMLInputElement>) => {
    let files = e?.target?.files ? e?.target?.files[0] : null
    setFile(files)
    if (files) {
        setValue("fichier",files)
    }
  }

  const onChangeAddress = (val: Data) => {
    setAddress(val?.rue);
    setValue(`adresse`, val?.rue);
    setValue(`latitude`, val?.latitude);
    setValue(`longitude`, val?.longitude);
    if (val?.ville) {
      setValue("ville", val?.ville);
    }
  };

  const handleChangePhone = (item: any, country: any) => {
    setPhone(item);
    setCode(country?.countryCode);
    if (parseInt(item) !== parseInt(country?.dialCode)) {
      setValue("telephone", item);
    } else {
      setValue("telephone", "");
    }
  };

  useEffect(() => {
    if (isSuccess) {
      Swal.fire({
        icon: "success",
        title: !item
          ? "Patient ajouté avec succès"
          : "Le patient a été modifié avec succès",
        iconColor: Color.success,
        showConfirmButton: false,
        timer: 2000,
      }).then(() => {
        if (isMedecin(user)) {
          navigate("/medecin/patients");
        } else if (isCabinet(user)) {
          navigate("/cabinet/patients");
        } else {
          navigate("/secretaire-medical/patients");
        }
      });
    }
    const err = error as any;
    if (isError) {
      if (err?.data?.email?.includes("user with this email already exists.")) {
        Swal.fire({
          icon: "error",
          title: "Un utilisateur avec cet email exite déjà!",
          showConfirmButton: false,
          timer: 2000,
        });
      } else {
        Swal.fire({
          icon: "error",
          title: err?.data?.message
            ? err?.data?.message
            : `Erreur de statut ${err?.status}`,
          showConfirmButton: false,
          timer: 5000,
        });
      }
    }
  }, [isLoading]);

  useEffect(() => {
    // console.log("cabinet", errors);
    cleannerError(errors, clearErrors);
  }, [errors]);

  useEffect(() => {
    if (item?.id) {
      
      let fields: (keyof UserFormData)[] = [
        "nom",
        "prenom",
        "telephone",
        "email",
        "adresse",
        "ville",
        "structure",
        "civilite",
        "date_de_naissance"
      ];

      if (item?.telephone) {
        setPhone(item.telephone);
      }

      if (item?.adresse) {
        setAddress(item.adresse);
      }

      for (let field of fields) {
        register(field);
        if (field !== "structure") {
          setValue(field, item[field]);
        } else {
          if (item[field]) setValue(field, item[field]?.id);
        }
      }
    }
  }, [item]);

  const onSubmit = async (data: UserFormData) => {
    const fd = new FormData();
    for (let key of Object.keys(data)) {
      if (key === "fichier") {
        if (data?.fichier) {
          fd.append(key, data?.fichier);
        }
      } else {
        fd.append(key, data[key]);
      }
    }
    await sendData({
      slug: item?.slug,
      data: fd,
    });
  };

  return {
    register,
    errors: errors,
    onSubmit: handleSubmit(onSubmit),
    setValue,
    isLoading,
    handleChangePhone,
    phone,
    address,
    onChangeAddress,
    onChangeFile,
    file
  };
}

export default UseAddOrEditPatient;

export function UseArchivePatient(item: IUser) {
  const [sendData] = useAddOrEditPatientMutation();
  const onArchive = async () => {
    await Swal.fire({
      title: `Êtes-vous sûr de vouloir ${
        item?.is_archive ? "désarchiver" : "archiver"
      } ce patient ?`,
      icon: "warning",
      showCancelButton: true,
      confirmButtonText: "OUI",
      cancelButtonText: "NON",
      showLoaderOnConfirm: true,
      iconColor: Color.themeColor,
      confirmButtonColor: Color.themeColor,
      preConfirm: () => {
        return sendData({
          slug: item?.slug,
          data: { is_archive: !item?.is_archive },
        });
      },
      allowOutsideClick: () => !Swal.isLoading(),
    }).then((result: any) => {
      if (result?.isConfirmed) {
        if (result?.value && !result?.value?.error) {
          Swal.fire({
            icon: "success",
            title: `Le patient a été ${
              item?.is_archive ? "désarchivé" : "archivé"
            } avec succès!`,
            iconColor: Color.themeColor,
            showConfirmButton: false,
            timer: 1500,
          });
        } else {
          let err = result?.value?.error;
          Swal.fire({
            icon: "error",
            title: err?.data?.message
              ? err?.data?.message
              : `Une erreur de statut ${err?.status} est survenue!`,
            showConfirmButton: false,
            timer: 5000,
          });
        }
      }
    });
  };
  return onArchive;
}